<script lang="ts">
import { defineComponent, PropType } from 'vue';

export type AppSelectOption = {
  value: string | number;
  label: string;
  disabled?: boolean;
};

export default defineComponent({
  name: 'AppSelect',
  props: {
    modelValue: {
      type: [String, Number] as PropType<string | number | null>,
      default: null,
    },
    options: {
      type: Array as PropType<AppSelectOption[]>,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: '—',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String as PropType<'compact' | 'default' | 'events-form' | 'filter'>,
      default: 'default',
    },
    ariaLabel: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      open: false,
      menuStyle: {} as Record<string, string>,
    };
  },
  computed: {
    normalizedValue(): string {
      if (this.modelValue === null || this.modelValue === undefined || this.modelValue === '') {
        return '';
      }
      return String(this.modelValue);
    },
    selectedLabel(): string {
      const found = this.options.find((o) => String(o.value) === this.normalizedValue);
      return found?.label ?? '';
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeydown);
    window.addEventListener('resize', this.onViewportChange);
    window.addEventListener('scroll', this.onViewportChange, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
    window.removeEventListener('resize', this.onViewportChange);
    window.removeEventListener('scroll', this.onViewportChange, true);
  },
  methods: {
    toggle() {
      if (this.disabled) return;
      this.open = !this.open;
      if (this.open) {
        this.$nextTick(() => this.updateMenuPosition());
      }
    },
    close() {
      this.open = false;
    },
    selectOption(option: AppSelectOption) {
      if (option.disabled) return;
      const raw = option.value;
      this.$emit('update:modelValue', raw);
      this.$emit('change', raw);
      this.close();
    },
    isSelected(option: AppSelectOption) {
      return String(option.value) === this.normalizedValue;
    },
    updateMenuPosition() {
      const trigger = this.$refs.trigger as HTMLElement | undefined;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      this.menuStyle = {
        top: `${rect.bottom + 4}px`,
        left: `${rect.left}px`,
        minWidth: `${rect.width}px`,
        width: 'max-content',
      };
    },
    onDocumentClick(event: MouseEvent) {
      if (!this.open) return;
      const root = this.$refs.root as HTMLElement | undefined;
      const menu = this.$refs.menu as HTMLElement | undefined;
      const target = event.target as Node;
      if (root?.contains(target) || menu?.contains(target)) return;
      this.close();
    },
    onDocumentKeydown(event: KeyboardEvent) {
      if (event.key === 'Escape') this.close();
    },
    onViewportChange() {
      if (this.open) this.updateMenuPosition();
    },
  },
});
</script>

<template>
  <div
    ref="root"
    class="app-select"
    :class="[
      `app-select--${variant}`,
      { 'app-select--open': open, 'app-select--disabled': disabled },
    ]"
  >
    <button
      ref="trigger"
      type="button"
      class="app-select__trigger"
      :disabled="disabled"
      :aria-label="ariaLabel || undefined"
      aria-haspopup="listbox"
      :aria-expanded="open"
      @click.stop="toggle"
    >
      <span v-if="variant === 'events-form'" class="app-select__events-lead" aria-hidden="true">
        <i class="bi bi-chevron-down"></i>
      </span>
      <span class="app-select__value">{{ selectedLabel || placeholder }}</span>
      <span class="app-select__chevron" aria-hidden="true"></span>
    </button>

    <Teleport to="body">
      <ul
        v-if="open"
        ref="menu"
        class="app-select__list"
        :class="`app-select__list--${variant}`"
        role="listbox"
        :style="menuStyle"
      >
        <li
          v-for="option in options"
          :key="String(option.value)"
          role="option"
          class="app-select__option"
          :class="{
            'app-select__option--selected': isSelected(option),
            'app-select__option--disabled': option.disabled,
          }"
          :aria-selected="isSelected(option)"
          @click.stop="selectOption(option)"
        >
          {{ option.label }}
        </li>
      </ul>
    </Teleport>
  </div>
</template>
